/**
 * `GET /invitations`: every invitation addressed to the caller's own
 * verified email.
 *
 * The endpoint itself never refuses -- an unverified or emailless caller
 * gets a 200 with an empty list, so a 403 on a page an invitee legitimately
 * reached does not read as "you are not allowed here" (api's own
 * `list_my_invitations`). That means this hook cannot tell "genuinely no
 * invitations" apart from "not verified yet" by reading the response: the
 * client has to hold off asking until it already knows the caller is
 * verified, which is what `enabled` does.
 *
 * `enabled` names the three states it fires in, the same discipline
 * `useMe`'s own `enabled` documents: signed in, with a verified address, and
 * with `useMe` itself resolved -- `POST`ing an accept before the caller's own
 * `users` row is confirmed to exist would be refused (`api`'s
 * `_resolve_user_id`, "no such user"), so the list is not worth fetching
 * ahead of that either.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useAuthState } from '../../session/authState';
import { useApiClient } from '../../session/client';
import { useMe } from '../../session/session';
import { type MyInvitation } from './schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const MY_INVITATIONS_QUERY_KEY = ['invitations', 'mine'] as const;

export function useMyInvitations(): UseQueryResult<MyInvitation[], ApiFailureError> {
  const client = useApiClient();
  const session = useAuthState();
  const me = useMe();

  return useQuery<MyInvitation[], ApiFailureError>({
    queryKey: MY_INVITATIONS_QUERY_KEY,
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.myInvitations, {}),
        GET_ROUTES.myInvitations.schema,
      );
      return items;
    },
    enabled:
      session.status === 'signed-in' &&
      session.user?.emailVerified === true &&
      (me.status === 'success' || me.status === 'error'),
  });
}
